import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient, { axiosWithAuth } from '@/services/axios';
import { userTypes } from '@/constants/types';
import { getPhoneWithCode } from '@/utils/string';

export const getDivisions = createAsyncThunk(
  'shippingAddress/getDivisions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/order/region/divisions`);
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getDistricts = createAsyncThunk(
  'shippingAddress/getDistricts',
  async (divisionId, { getState, rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/order/region/districts/${divisionId}`,
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getUpazillas = createAsyncThunk(
  'shippingAddress/getUpazillas',
  async (districtId, { getState, rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/order/region/upazillas/${districtId}`,
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getAreas = createAsyncThunk(
  'shippingAddress/getAreas',
  async (upazillaId, { getState, rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/order/region/areas/${upazillaId}`,
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getUserAddresses = createAsyncThunk(
  'shippingAddress/getUserAddresses',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axiosWithAuth().get(
        `/api/order/shipping-addresses/user`,
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const postShippingAddress = createAsyncThunk(
  'shippingAddress/postShippingAddress',
  async ({ createAddressData, formType, userType }, { rejectWithValue }) => {
    try {
      const paths = {
        loggedInUser: `/api/order/shipping-address/user`,
        guest: `/api/order/shipping-address`,
      };

      let response;

      if (userType === userTypes.guest) {
        response = await axiosClient.post(paths.guest, createAddressData);
      } else if (userType === userTypes.loggedIn) {
        response = await axiosWithAuth().post(
          paths.loggedInUser,
          createAddressData,
        );
      }

      const updatedResponse = {
        ...response.data,
        formType,
        userType,
      };

      return await updatedResponse;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// description
// shippingId => update by id
// shippingAddressData => update this data in db
// formType => identify which form (billing, shipping)
// updatedFormData => will directly update in redux state
// userType => guest / loggedIn user
export const EditShippingAddress = createAsyncThunk(
  'shippingAddress/EditShippingAddress',
  async (
    { shippingId, shippingAddressData, formType, updatedFormData, userType },
    { rejectWithValue },
  ) => {
    try {
      const paths = {
        loggedInUser: `/api/order/shipping-address/user/${shippingId}`,
        guest: `/api/order/shipping-address/${shippingId}`,
      };

      let response;

      if (userType === userTypes.guest) {
        response = await axiosClient.put(paths.guest, shippingAddressData);
      } else if (userType === userTypes.loggedIn) {
        response = await axiosWithAuth().put(
          paths.loggedInUser,
          shippingAddressData,
        );
      }

      if (!response.status) {
        throw new Error(
          response.message || 'Failed to update shipping address',
        );
      }

      const updatedResponse = {
        formType,
        updatedFormData,
      };

      return updatedResponse;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const DeleteShippingAddress = createAsyncThunk(
  'shippingAddress/DeleteShippingAddress',
  async ({ shippingId, userType }, { rejectWithValue }) => {
    try {
      const url = `/api/order/shipping-address/${shippingId}`;

      let response;

      if (userType === userTypes.loggedIn) {
        response = await axiosClient.delete(url);
      }

      if (!response.status) {
        throw new Error(
          response.message || 'Failed to delete shipping address',
        );
      }

      return shippingId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getShippingFees = createAsyncThunk(
  'shippingAddress/getShippingFees',
  async ({ data, cart }, { rejectWithValue }) => {
    try {
      const products = cart.map((item) => ({
        id: item.id,
        quantity: item.cart_quantity,
      }));

      const requestData = { ...data, products };

      const response = await axiosClient.post(
        `/api/order/shipping-fees`,
        requestData,
      );

      return await response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const convertGuestShippingAddressToAuthenticateUserShippingAddress =
  createAsyncThunk(
    'shippingAddress/convertGuestShippingAddressToAuthenticateUserShippingAddress',
    async ({ shippingAddressData }, { getState, rejectWithValue }) => {
      try {
        const { isLoggedIn } = getState().auth;

        if (!isLoggedIn) {
          return;
        }

        const shippingId = shippingAddressData.id;

        const updatedShippingAddressData = {
          name: shippingAddressData.name,
          phone:
            shippingAddressData?.phone[0] === '+'
              ? shippingAddressData?.phone
              : getPhoneWithCode(shippingAddressData?.phone),
          email: shippingAddressData.email,
          address: shippingAddressData.address,
          floor: shippingAddressData?.floor
            ? Number(shippingAddressData?.floor)
            : 1,
          division_id: shippingAddressData?.division_id
            ? shippingAddressData.division_id
            : shippingAddressData?.division?.id,
          district_id: shippingAddressData?.district_id
            ? shippingAddressData.district_id
            : shippingAddressData?.district?.id,
          upazilla_id: shippingAddressData?.upazilla_id
            ? shippingAddressData.upazilla_id
            : shippingAddressData?.upazilla?.id,
          area_id: shippingAddressData?.area_id
            ? shippingAddressData.area_id
            : shippingAddressData?.area?.id,
        };

        // used to remove empty field before edit data
        Object.keys(updatedShippingAddressData).forEach((key) => {
          if (
            !updatedShippingAddressData[key] &&
            updatedShippingAddressData[key] !== false
          ) {
            delete updatedShippingAddressData[key];
          }
        });

        // converted address will always not default
        updatedShippingAddressData.is_default = false;

        const paths = {
          loggedInUser: `/api/order/shipping-address/user/${shippingId}`,
        };

        const response = await axiosWithAuth().put(
          paths.loggedInUser,
          updatedShippingAddressData,
        );

        if (!response.status) {
          throw new Error(
            response.message || 'Failed to covert shipping address',
          );
        }

        // const updatedResponse = {
        //   formType,
        //   updatedFormData,
        // };

        console.log({ response });

        return response;
      } catch (error) {
        return rejectWithValue(error.message);
      }
    },
  );
